import { useCallback, useSyncExternalStore } from 'react';

/*
 * Media queries, read from React.
 *
 * `useSyncExternalStore` rather than the `useState` + `useEffect` pair this
 * replaces, and the difference is visible rather than academic. The effect
 * version cannot know the answer until after the first paint: it renders once
 * with the initial state, the effect runs, `setState` schedules a second
 * render. On a phone that meant the desktop rail was painted and then swapped
 * for the drawer on every single load — a flash of the wrong layout, every
 * time, forever, because the bug is in the shape of the hook and not in a value
 * anyone could tune.
 *
 * `useSyncExternalStore` asks the store during the first render instead, so the
 * first paint is already correct. It is also the API that makes tearing
 * impossible under concurrent rendering, which matters here for a reason
 * specific to this hook: two components reading the same breakpoint must not
 * disagree about it mid-render, and the rail plus the page it sits beside are
 * exactly that pair.
 *
 * The px values below are duplicated in CSS, which is not something a stylesheet
 * and a module can share — a custom property cannot appear in a media prelude,
 * and there is no build step here that would inline one. `responsive.test.ts`
 * asserts the parity instead: every number in `MEDIA` has to appear in a real
 * `@media` prelude somewhere in `src/`. That guard exists because this drifted
 * once already, as a lone `DRAWER_QUERY` string inside `Sidebar.tsx` that
 * nothing tied to `shell.css`.
 */

/**
 * The queries the app is allowed to ask about.
 *
 * Named rather than spelled out at each call site so the JS side has one
 * copy of each number, and so the parity guard has something to read. See the
 * breakpoint ladder comment in `tokens.css` for what each tier is *for*; this
 * is only the subset JavaScript needs to know about, which is deliberately
 * small — a layout decision belongs in CSS unless a component genuinely
 * behaves differently, and `drawer` is the one that does.
 *
 * `touch` and `coarse` are kept apart on purpose. `(hover: none)` means "there
 * is no hover, so anything hidden behind one is unreachable"; `(pointer:
 * coarse)` means "the pointer is a fingertip, so hit areas need to be bigger".
 * An iPad with a trackpad reports `hover: hover` and `pointer: fine`, and a
 * touchscreen laptop reports `hover: hover` with a coarse *secondary* pointer —
 * collapsing the two into one "is mobile" flag gets both of those wrong.
 */
export const MEDIA = {
  /** Below this the rail stops being a rail and becomes an off-canvas drawer. */
  drawer: '(max-width: 880px)',
  /** Phone tier: one column, controls go full width. */
  phone: '(max-width: 640px)',
  /** No hover at all — hover-gated affordances have to be revealed. */
  touch: '(hover: none)',
  /** Fingertip-sized pointer — hit areas have to grow. */
  coarse: '(pointer: coarse)',
} as const;

export type MediaQueryString = (typeof MEDIA)[keyof typeof MEDIA];

/*
 * jsdom implements no `matchMedia`, so an unguarded `window.matchMedia(q)`
 * throws before a single assertion runs in every test that mounts anything
 * using this hook. The guard is load-bearing for the suite, not defensive
 * coding for a hypothetical SSR pass.
 *
 * `typeof … === 'function'` rather than `'matchMedia' in window`, and the
 * difference is not pedantry: under jsdom the property SLOT exists and its
 * value is `undefined`, so an `in` check — or a truthiness check on `window`
 * alone — passes and then throws on the call. Verified in
 * `useMediaQuery.test.ts`, which asserts the jsdom shape directly so that a
 * future jsdom actually implementing this is a red test rather than a guard
 * that has quietly stopped guarding.
 */
function canMatch(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

/**
 * `true` while `query` matches, kept in sync with the viewport.
 *
 * Correct on the very first render, including the first render after a resize
 * that happened while the component was unmounted.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!canMatch()) return () => {};
      const list = window.matchMedia(query);
      list.addEventListener('change', onStoreChange);
      return () => list.removeEventListener('change', onStoreChange);
    },
    [query],
  );

  /* Re-reads rather than caching a MediaQueryList: browsers hand back the same
     object for the same string, and a test that swaps `window.matchMedia`
     between renders would otherwise keep answering from the old one. The return
     is a primitive, so React's "getSnapshot should be cached" loop detector is
     satisfied by value. */
  const getSnapshot = useCallback(
    () => (canMatch() ? window.matchMedia(query).matches : false),
    [query],
  );

  /* No viewport to measure, so nothing matches. Every query here is a
     `max-width` or a device capability, and answering `false` renders the
     desktop, pointer-having layout — the safe default for a machine that is not
     a device at all. */
  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
